import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    Stack,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Tooltip,
    Badge,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider
} from "@mui/material";
import {
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Compare as CompareIcon,
    History as HistoryIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Close as CloseIcon,
    Circle as CircleIcon
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { serializationService } from "../services/serialization/serializationService";
import { auditService } from "../services/auditService";
// eslint-disable-next-line no-unused-vars
import { adminAuditService } from "../services/adminAuditService";

const SerializationDashboard = () => {
    // eslint-disable-next-line no-unused-vars
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [entities, setEntities] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [filters, setFilters] = useState({
        entityType: "all",
        userId: "all",
        dateRange: "7d",
        actionType: "all",
        status: "all"
    });
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [entityHistory, setEntityHistory] = useState([]);
    const [comparisonData, setComparisonData] = useState(null);

    const loadDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            
            // Load entities
            const entities = await serializationService.getAllEntities();
            setEntities(entities);
            
            // Load audit logs
            const logs = await auditService.getRecentLogs(100);
            setAuditLogs(logs);
            
            // Calculate stats
            // Eliminar o comentar la línea: const stats = calculateStats(entities, logs);
            
        } catch (error) {
            // Error loading dashboard data
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    // Eliminar la función generateMockEntities si no se usa en el código.

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleEntitySelect = async (entity) => {
        setSelectedEntity(entity);
        
        // Load entity history
        try {
            const history = await serializationService.getVersionHistory(entity.id, entity.type);
            setEntityHistory(history);
        } catch (error) {
            // Error loading entity history
            setEntityHistory([]);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleCompareVersions = (entity1, entity2) => {
        const differences = serializationService.compareVersions(entity1, entity2);
        setComparisonData(differences);
    };

    const getStatusColor = (status) => {
        switch (status) {
        case "serialized": return "success";
        case "needs_migration": return "warning";
        case "validation_error": return "error";
        default: return "default";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
        case "serialized": return <CheckCircleIcon />;
        case "needs_migration": return <WarningIcon />;
        case "validation_error": return <ErrorIcon />;
        default: return <InfoIcon />;
        }
    };

    const filteredEntities = entities.filter(entity => {
        if (filters.entityType !== "all" && entity.type !== filters.entityType) return false;
        if (filters.status !== "all" && entity.status !== filters.status) return false;
        return true;
    });

    const filteredLogs = auditLogs.filter(log => {
        if (filters.userId !== "all" && log.userId !== filters.userId) return false;
        if (filters.actionType !== "all" && log.action !== filters.actionType) return false;
        return true;
    });

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Stack spacing={4}>
                {/* Header */}
                <Box>
                    <Typography variant="h3" color="primary" sx={{ fontWeight: "bold", mb: 1 }}>
                        Dashboard de Serialización & Trazabilidad
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Control centralizado del sistema de versionado y auditoría
                    </Typography>
                </Box>

                {/* Stats Cards */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    Total Entidades
                                </Typography>
                                <Typography variant="h4" color="primary">
                                    {entities.length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    Serializadas
                                </Typography>
                                <Typography variant="h4" color="success.main">
                                    {entities.filter(e => e.status === "serialized").length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    Migraciones Pendientes
                                </Typography>
                                <Typography variant="h4" color="warning.main">
                                    {entities.filter(e => e.status === "needs_migration").length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    Errores de Validación
                                </Typography>
                                <Typography variant="h4" color="error.main">
                                    {entities.filter(e => e.status === "validation_error").length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    Últimas 24h
                                </Typography>
                                <Typography variant="h4" color="info.main">
                                    {auditLogs.filter(log => new Date(log.timestamp) > new Date(Date.now() - 24 * 60 * 60 * 1000)).length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filters */}
                <Card>
                    <CardHeader 
                        title="Filtros"
                        action={
                            <IconButton onClick={loadDashboardData}>
                                <RefreshIcon />
                            </IconButton>
                        }
                    />
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Tipo de Entidad</InputLabel>
                                    <Select
                                        value={filters.entityType}
                                        onChange={(e) => handleFilterChange("entityType", e.target.value)}
                                    >
                                        <MenuItem value="all">Todos</MenuItem>
                                        <MenuItem value="User">Usuarios</MenuItem>
                                        <MenuItem value="Opportunity">Oportunidades</MenuItem>
                                        <MenuItem value="Investment">Inversiones</MenuItem>
                                        <MenuItem value="Transaction">Transacciones</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Estado</InputLabel>
                                    <Select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange("status", e.target.value)}
                                    >
                                        <MenuItem value="all">Todos</MenuItem>
                                        <MenuItem value="serialized">Serializadas</MenuItem>
                                        <MenuItem value="needs_migration">Migración Pendiente</MenuItem>
                                        <MenuItem value="validation_error">Error de Validación</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Rango de Fechas</InputLabel>
                                    <Select
                                        value={filters.dateRange}
                                        onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                                    >
                                        <MenuItem value="1d">Último día</MenuItem>
                                        <MenuItem value="7d">Última semana</MenuItem>
                                        <MenuItem value="30d">Último mes</MenuItem>
                                        <MenuItem value="90d">Últimos 3 meses</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    fullWidth
                                    onClick={() => {/* Export functionality */}}
                                >
                                    Exportar Datos
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Main Content Tabs */}
                <Card>
                    <CardContent sx={{ p: 0 }}>
                        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                            <Tab label="Entidades" />
                            <Tab label="Timeline" />
                            <Tab label="Comparación" />
                            <Tab label="Auditoría" />
                        </Tabs>
                    </CardContent>
                </Card>

                {/* Tab Content */}
                <Box>
                    {activeTab === 0 && (
                        <Card>
                            <CardContent>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>ID</TableCell>
                                                <TableCell>Tipo</TableCell>
                                                <TableCell>Versión</TableCell>
                                                <TableCell>Estado</TableCell>
                                                <TableCell>Última Actualización</TableCell>
                                                <TableCell>Serializado Por</TableCell>
                                                <TableCell>Errores</TableCell>
                                                <TableCell>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredEntities.map((entity) => (
                                                <TableRow 
                                                    key={entity.id}
                                                    hover
                                                    onClick={() => handleEntitySelect(entity)}
                                                    sx={{ cursor: "pointer" }}
                                                >
                                                    <TableCell>{entity.id}</TableCell>
                                                    <TableCell>
                                                        <Chip label={entity.type} size="small" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={entity.version} 
                                                            color="primary" 
                                                            size="small" 
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            icon={getStatusIcon(entity.status)}
                                                            label={entity.status}
                                                            color={getStatusColor(entity.status)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {entity.serializedAt ? new Date(entity.serializedAt.toDate ? entity.serializedAt.toDate() : entity.serializedAt).toLocaleString() : "N/A"}
                                                    </TableCell>
                                                    <TableCell>{entity.serializedBy}</TableCell>
                                                    <TableCell>
                                                        {entity.validationErrors > 0 ? (
                                                            <Badge badgeContent={entity.validationErrors} color="error">
                                                                <ErrorIcon color="error" />
                                                            </Badge>
                                                        ) : (
                                                            <CheckCircleIcon color="success" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={1}>
                                                            <Tooltip title="Ver Historial">
                                                                <IconButton size="small">
                                                                    <HistoryIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Comparar Versiones">
                                                                <IconButton size="small">
                                                                    <CompareIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 1 && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Timeline de Cambios
                                </Typography>
                                <List>
                                    {filteredLogs.slice(0, 10).map((log, index) => (
                                        <ListItem key={index}>
                                            <ListItemIcon>
                                                <CircleIcon fontSize="small" color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="subtitle2">
                                                        {log.action}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="body2" color="text.secondary">
                                                        {log.entityType} - {log.entityId}
                                                    </Typography>
                                                }
                                            />
                                            <ListItemText
                                                secondaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                                                secondary={
                                                    <Typography variant="body2">
                                                        Por: {log.userId}
                                                    </Typography>
                                                }
                                            />
                                            <Divider variant="inset" component="li" />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 2 && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Comparación de Versiones
                                </Typography>
                                {comparisonData ? (
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                            Diferencias entre {comparisonData.metadata.version1} y {comparisonData.metadata.version2}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={4}>
                                                <Typography variant="h6" color="success.main">
                                                    Campos Agregados
                                                </Typography>
                                                {Object.entries(comparisonData.added).map(([key, value]) => (
                                                    <Typography key={key} variant="body2">
                                                        {key}: {JSON.stringify(value)}
                                                    </Typography>
                                                ))}
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="h6" color="error.main">
                                                    Campos Removidos
                                                </Typography>
                                                {Object.entries(comparisonData.removed).map(([key, value]) => (
                                                    <Typography key={key} variant="body2">
                                                        {key}: {JSON.stringify(value)}
                                                    </Typography>
                                                ))}
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="h6" color="warning.main">
                                                    Campos Modificados
                                                </Typography>
                                                {Object.entries(comparisonData.changed).map(([key, change]) => (
                                                    <Typography key={key} variant="body2">
                                                        {key}: {JSON.stringify(change.from)} → {JSON.stringify(change.to)}
                                                    </Typography>
                                                ))}
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ) : (
                                    <Typography color="text.secondary">
                                        Selecciona dos versiones para comparar
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 3 && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Logs de Auditoría
                                </Typography>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Timestamp</TableCell>
                                                <TableCell>Usuario</TableCell>
                                                <TableCell>Acción</TableCell>
                                                <TableCell>Entidad</TableCell>
                                                <TableCell>Detalles</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredLogs.slice(0, 20).map((log, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        {log.timestamp ? (log.timestamp.toDate ? log.timestamp.toDate().toLocaleString() : new Date(log.timestamp).toLocaleString()) : "N/A"}
                                                    </TableCell>
                                                    <TableCell>{log.userId}</TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={log.action} 
                                                            size="small"
                                                            color={log.action.includes("error") ? "error" : "primary"}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {log.entityType} - {log.entityId}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" noWrap>
                                                            {JSON.stringify(log.details).substring(0, 50)}...
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}
                </Box>

                {/* Entity History Sidebar */}
                {selectedEntity && (
                    <Card>
                        <CardHeader 
                            title={`Historial de ${selectedEntity.type} - ${selectedEntity.id}`}
                            action={
                                <IconButton onClick={() => setSelectedEntity(null)}>
                                    <CloseIcon />
                                </IconButton>
                            }
                        />
                        <CardContent>
                            <List>
                                {entityHistory.map((version, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon>
                                            <CircleIcon fontSize="small" color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle2">
                                                    Versión {version.version}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography variant="body2" color="text.secondary">
                                                    {version.timestamp ? (version.timestamp.toDate ? version.timestamp.toDate().toLocaleString() : new Date(version.timestamp).toLocaleString()) : "N/A"}
                                                </Typography>
                                            }
                                        />
                                        <ListItemText
                                            secondaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                                            secondary={
                                                <Typography variant="body2">
                                                    Por: {version.serializedBy}
                                                </Typography>
                                            }
                                        />
                                        {version.changeReason && (
                                            <ListItemText
                                                secondaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                                                secondary={
                                                    <Typography variant="body2">
                                                        Razón: {version.changeReason}
                                                    </Typography>
                                                }
                                            />
                                        )}
                                        <Divider variant="inset" component="li" />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                )}
            </Stack>
        </Container>
    );
};

export default SerializationDashboard; 